import * as React from 'react';

import { Error } from 'components/error/Error';
import Form from 'components/form/Form';
import GridNew from 'components/grid/GridNew';
import { Loader } from 'components/loader/Loader';
import { STATUSES } from 'core/api';
import { useAppSelector, useAppDispatch } from 'core/store/createStore';

import { formConfig, gridConfig } from './config';
import { ICountryValues, IProps } from './interfaces';
import {
  editCountry,
  fetchCountries,
  fetchCountryInfo,
  resetCountryInfo,
} from './redux';

import styles from './Countries.module.styl';

const Countries: React.FC<IProps> = ({
  history,
  match: {
    params: { id },
  },
  location: { search, hash },
}) => {
  const [isSubmiting, setIsSubmiting] = React.useState(false);
  const { list: countries, item: country } = useAppSelector(
    state => state.countries,
  );
  const dispatch = useAppDispatch();
  const getInitialValues = () => {
    const data = country.data.data || {};

    return {
      ...data,
      languages: data.languages ? data.languages.split(',') : [],
    };
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const onSubmit = async (values: ICountryValues) => {
    setIsSubmiting(true);
    if (!id) {
      setIsSubmiting(false);
      return;
    }
    const languages = [
      'en',
      ...values.languages.filter(item => item !== 'en'),
    ].join(',');

    const preparedValues = {
      ...values,
      languages,
    };

    dispatch(editCountry(id, preparedValues));

    await sleep(3000);
    dispatch(
      fetchCountries({
        hash,
        search,
      }),
    );

    dispatch(fetchCountryInfo(id));
    setIsSubmiting(false);
  };
  const validate = (values: ICountryValues) => {
    const { languages } = values;

    if (!languages.length) {
      return {
        languages: 'At least one language should be defined',
      };
    }

    if (!languages.includes('en')) {
      return {
        languages: 'English language should exist',
      };
    }

    return undefined;
  };
  const handleSearch = (searchPhrase: string) => {
    history.push(`/admin/countries#${searchPhrase}`);
  };
  const getRenderedForm = () => {
    switch (country.status) {
      case STATUSES.REQUEST:
        return <Loader />;
      case STATUSES.FAILED:
        return <Error>{country.error}</Error>;
      case STATUSES.INITIAL:
        return id ? null : (
          <div className={styles.message}>
            <p>Please choose the country from the list</p>
          </div>
        );
      case STATUSES.SUCCESS:
        const initialValues = getInitialValues();
        return (
          <Form
            config={{ ...formConfig, title: initialValues.name }}
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            disableSubmitButton={({ pristine, isDirty }: any) =>
              (pristine && !isDirty) || isSubmiting
            }
            showLoader={isSubmiting}
          />
        );
      default:
        return null;
    }
  };
  const getRenderedList = () => {
    const searchPhrase = hash ? hash.split('#')[1] : '';

    switch (countries.status) {
      case STATUSES.REQUEST:
        return <Loader />;
      case STATUSES.FAILED:
        return <Error>{countries.error}</Error>;
      case STATUSES.SUCCESS:
        return (
          <GridNew
            pagination={countries.data.pagination}
            data={countries.data.items}
            config={gridConfig}
            urlPrefix={'/admin/countries'}
            onSearch={handleSearch}
            searchPhrase={searchPhrase}
          />
        );
      default:
        return null;
    }
  };

  React.useEffect(() => {
    dispatch(fetchCountries({ hash, search }));
  }, [hash, search, dispatch]);
  React.useEffect(() => {
    if (!id) {
      dispatch(resetCountryInfo());
    } else {
      dispatch(fetchCountryInfo(id));
    }

    return () => {
      dispatch(resetCountryInfo());
    };
  }, [id, dispatch]);

  return (
    <div className={styles.root}>
      {getRenderedForm()}
      {getRenderedList()}
    </div>
  );
};

export default Countries;
